"use client";

import {useEffect, useState} from "react";

import Icon from "@/components/library/Icon";
import Image from "next/image";
import Modal from "@/components/library/Modal";
import {VoteUrgency} from "@/types/integrations";
import clsx from "clsx";
import {useTranslation} from "react-i18next";
import {type Importance, IMPORTANCE_OPTIONS, IMPORTANCE_TO_URGENCY} from "./importance-config";

interface IntegrationComingSoonModalProps {
   open: boolean;
   onClose: () => void;
   onSubmit: (request: ComingSoonRequest) => Promise<void>;
   integration: {
      id: string;
      name: string;
      logoUrl?: string;
      description?: string;
   } | null;
}

export interface ComingSoonRequest {
   integrationId: string;
   integrationName: string;
   useCase: string;
   urgency: VoteUrgency;
}

const USE_CASE_EXAMPLE_KEYS = [
   "marketplaceUseCaseSync",
   "marketplaceUseCaseTrigger",
   "marketplaceUseCaseLog",
   "marketplaceUseCaseUpdate",
   "marketplaceUseCasePull",
];

export default function IntegrationComingSoonModal({
   open,
   onClose,
   onSubmit,
   integration,
}: Readonly<IntegrationComingSoonModalProps>) {
   const {t: ct} = useTranslation("common");
   const {t} = useTranslation("partners");
   const [useCase, setUseCase] = useState("");
   const [importance, setImportance] = useState<Importance>("important");
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [isSubmitted, setIsSubmitted] = useState(false);
   const [logoError, setLogoError] = useState(false);

   useEffect(() => {
      if (open) {
         setUseCase("");
         setImportance("important");
         setIsSubmitted(false);
         setLogoError(false);
      }
   }, [open, integration?.id]);

   const handleOpenChange = (isOpen: boolean) => {
      if (!isOpen) onClose();
   };

   const handleSubmit = async () => {
      if (!integration || !useCase.trim()) return;

      setIsSubmitting(true);
      try {
         await onSubmit({
            integrationId: integration.id,
            integrationName: integration.name,
            useCase: useCase.trim(),
            urgency: IMPORTANCE_TO_URGENCY[importance],
         });
         setIsSubmitted(true);
      } finally {
         setIsSubmitting(false);
      }
   };

   const placeholderLetter = integration?.name?.charAt(0).toUpperCase() ?? "?";

   if (isSubmitted) {
      return (
         <Modal
            open={open}
            onOpenChange={handleOpenChange}
            header={{title: t("marketplaceRequestSubmittedTitle")}}
            size="md"
            buttons={[
               {
                  type: "primary",
                  onClick: () => handleOpenChange(false),
                  children: t("marketplaceDone"),
               },
            ]}
         >
            <div className="flex flex-col items-center text-center py-6">
               <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
                  <Icon icon="check" className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
               </div>
               <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t("marketplaceThanksForInput")}
               </h3>
               <p className="text-sm text-gray-600 dark:text-gray-400 max-w-sm">
                  {t("marketplaceInterestRecorded", {name: integration?.name})}
               </p>
            </div>
         </Modal>
      );
   }

   return (
      <Modal
         open={open}
         onOpenChange={handleOpenChange}
         header={{
            title: integration?.name ?? t("marketplaceIntegration"),
            description: t("marketplaceComingSoonDescription"),
         }}
         size="md"
         buttons={[
            {
               type: "primary",
               onClick: () => {
                  handleSubmit();
               },
               disabled: !useCase.trim() || isSubmitting,
               children: isSubmitting ? t("marketplaceSubmitting") : t("marketplaceSubmit"),
            },
            {
               type: "secondary",
               onClick: () => handleOpenChange(false),
               children: ct("cancel"),
            },
         ]}
      >
         <div className="space-y-6">
            <div className="flex items-start gap-4">
               <div
                  className={clsx(
                     "w-14 h-14 shrink-0 rounded-xl",
                     "bg-gray-50 dark:bg-gray-700/50",
                     "border border-gray-100 dark:border-gray-600/30",
                     "flex items-center justify-center overflow-hidden"
                  )}
               >
                  {integration?.logoUrl && !logoError ? (
                     <Image
                        src={integration.logoUrl}
                        alt={`${integration.name} logo`}
                        width={32}
                        height={32}
                        className="w-8 h-8 object-contain"
                        unoptimized
                        onError={() => setLogoError(true)}
                     />
                  ) : (
                     <span className="text-xl font-bold text-gray-400 dark:text-gray-500">
                        {placeholderLetter}
                     </span>
                  )}
               </div>
               <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                     <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {integration?.name}
                     </h3>
                     <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full">
                        {t("marketplaceComingSoon")}
                     </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                     {t("marketplaceComingSoonNotAvailable")}
                  </p>
               </div>
            </div>

            <div>
               <label
                  htmlFor="use-case"
                  className="block text-sm font-medium text-gray-900 dark:text-white mb-2"
               >
                  {t("marketplaceWhatWouldYouWant", {name: integration?.name})}
               </label>

               <div className="mb-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t("marketplaceExamples")}</p>
                  <div className="flex flex-wrap gap-1.5">
                     {USE_CASE_EXAMPLE_KEYS.map((key) => {
                        const example = t(key);
                        return (
                        <button
                           key={key}
                           type="button"
                           onClick={() =>
                              setUseCase((prev) =>
                                 prev ? `${prev}\n• ${example}` : `• ${example}`
                              )
                           }
                           className={clsx(
                              "px-2.5 py-1 text-xs rounded-md",
                              "bg-gray-100 dark:bg-gray-700/50",
                              "text-gray-600 dark:text-gray-400",
                              "hover:bg-gailBlue/10 hover:text-gailBlue",
                              "transition-colors"
                           )}
                        >
                           {example}
                        </button>
                        );
                     })}
                  </div>
               </div>

               <textarea
                  id="use-case"
                  value={useCase}
                  onChange={(e) => setUseCase(e.target.value)}
                  placeholder={t("marketplaceDescribePlaceholder")}
                  rows={4}
                  className={clsx(
                     "w-full px-3 py-2.5 text-sm",
                     "border border-gray-200 dark:border-gray-600 rounded-lg",
                     "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100",
                     "placeholder:text-gray-400 dark:placeholder:text-gray-500",
                     "focus:ring-2 focus:ring-gailBlue/20 focus:border-gailBlue",
                     "transition-colors duration-200 resize-none"
                  )}
               />
            </div>

            <div>
               <fieldset>
                  <legend className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                     {t("marketplaceImportanceQuestionFull")}
                  </legend>
                  <div className="space-y-2">
                     {IMPORTANCE_OPTIONS.map((option) => (
                        <button
                           key={option.value}
                           type="button"
                           onClick={() => setImportance(option.value)}
                           className={clsx(
                              "w-full px-4 py-3 rounded-lg border text-left transition-all duration-200",
                              importance === option.value
                                 ? "bg-gailBlue/5 dark:bg-gailBlue/10 border-gailBlue ring-1 ring-gailBlue/20"
                                 : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
                           )}
                        >
                           <div className="flex items-center gap-3">
                              <div
                                 className={clsx(
                                    "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                                    importance === option.value
                                       ? "border-gailBlue"
                                       : "border-gray-300 dark:border-gray-500"
                                 )}
                              >
                                 {importance === option.value ? (
                                    <div className="w-2 h-2 rounded-full bg-gailBlue" />
                                 ) : null}
                              </div>
                              <div>
                                 <div
                                    className={clsx(
                                       "text-sm font-medium",
                                       importance === option.value
                                          ? "text-gailBlue"
                                          : "text-gray-900 dark:text-white"
                                    )}
                                 >
                                 {t(option.labelKey)}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                 {t(option.descriptionKey)}
                                 </div>
                              </div>
                           </div>
                        </button>
                     ))}
                  </div>
               </fieldset>
            </div>
         </div>
      </Modal>
   );
}
